import { useEffect, useState } from "react";
import { remoteUrl } from "@/services/remoteConfig";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import type { Book } from "@/types/book";
import { getCoverBlob } from "@/services/bookStorageService";
import { db } from "@/services/db";

interface Props {
  book: Book;
  onDelete: (id: string) => void;
  onRename: (id: string) => void;
  /** Shelf mode (2026-10-07). Thomas: "First covers only. More books."
   *  Smaller cell, and NO caption when a real cover image loaded — the
   *  title is already printed on the artwork, so a label underneath is
   *  duplication that costs a text row per book. The placeholder case
   *  keeps its text, because there the text IS the cover. */
  dense?: boolean;
}

export function BookCard({ book, onDelete, onRename, dense }: Props) {
  const [coverUrl, setCoverUrl] = useState<string | undefined>(undefined);
  const [serverCoverFailed, setServerCoverFailed] = useState(false);

  // Live count of highlights for this book — drives the "N highlights"
  // link below the progress bar so the badge stays in sync when a user
  // adds or deletes annotations elsewhere in the app.
  const highlightCount = useLiveQuery(
    () => db.highlights.where("bookId").equals(book.id).count(),
    [book.id],
    0,
  );

  useEffect(() => {
    let cancelled = false;
    let url: string | undefined;
    (async () => {
      const blob = await getCoverBlob(book.id);
      if (blob && !cancelled) {
        url = URL.createObjectURL(blob);
        setCoverUrl(url);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [book.id]);

  const pct =
    typeof book.progressPercent === "number"
      ? Math.round(book.progressPercent * 100)
      : 0;

  return (
    <div className={dense ? "relative flex flex-col" : "pv-surface pv-border relative flex flex-col rounded-md border p-3 shadow-sm"}>
      {/* One Link wraps cover + title — a single tap on either opens the
          book. Was wrapped in a `group` with hover:shadow + opacity-0
          hidden actions — on iOS the first tap was consumed by Safari's
          hover-detect, so the user had to tap twice to actually open
          the book. No more. */}
      <Link
        to={`/reader/${book.id}`}
        className={dense ? "flex flex-1 flex-col items-center gap-1 active:opacity-80" : "flex flex-1 flex-col items-center gap-3 active:opacity-80"}
      >
        <div className={dense ? "pv-surface-2 flex h-[128px] w-[90px] items-center justify-center overflow-hidden rounded" : "pv-surface-2 flex h-48 w-32 items-center justify-center overflow-hidden rounded shadow-inner"}>
          {/* ── COVER SOURCES, IN ORDER (2026-10-07) ─────────────────
              1. the locally-extracted blob — fastest, already works
              2. the server's extracted cover, for a book whose own
                 extraction found nothing (or predates it)
              3. the generated title cover
              Keeps the grid uniform whichever one answers. */}
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={book.title}
              className="h-full w-full object-cover"
              draggable={false}
            />
          ) : !serverCoverFailed && book.cloudSha256 ? (
            <img
              src={remoteUrl(`/lib/cover/${encodeURIComponent(book.cloudSha256.slice(0, 16))}`)}
              alt={book.title}
              className="h-full w-full object-cover"
              loading="lazy"
              draggable={false}
              onError={() => setServerCoverFailed(true)}
            />
          ) : (
            <FallbackCover title={book.title} author={displayAuthor(book.author)} />
          )}
        </div>
        {/* ── NO CAPTION ON THE SHELF, EVER (2026-10-07) ──────────────
            Thomas: "still Henry Moore extra text display on homepage".
            The gate used to be (!dense || !coverUrl), i.e. a book with NO
            cover kept its caption. But FallbackCover already draws the
            title AND author inside the tile — so that book rendered the
            same text twice, once as its generated cover and once again
            underneath. On a shelf of nineteen clean covers the one doubled
            tile reads as a fault, which is exactly what he saw.
            FallbackCover IS the generated cover: same dimensions, title
            centred, no gradient. So on the shelf the caption is never
            needed — the tile always carries its own identity. */}
        {!dense && (
        <div className="w-full text-center">
          <div className="pv-fg line-clamp-2 text-base font-semibold">
            {book.title}
          </div>
          {book.author && (
            <div className="pv-fg-soft line-clamp-1 text-sm">
              {displayAuthor(book.author)}
            </div>
          )}
          {/* 2026-10-07: language chip removed. Thomas has asked twice to
              take the language/translation furniture off this screen. */}
        </div>
        )}
      </Link>

      {/* ── NEVER SHOW 0% (2026-10-07) ────────────────────────────────
          Thomas, repeating an earlier ask: "don't show 0% on home page /
          reading bars". A book he has not opened has no honest figure, and
          an empty track with a bare "0%" is noise on a shelf of nineteen.
          Nothing is better than a zero.

          THIS IS A REGRESSION I CAUSED. The gate existed and was working;
          it was lost when I ran `git checkout -- BookCard.tsx` during the
          emergency rollback at 261007-0725. The fix had never been
          committed, so reverting to HEAD took it with it. Noted so the
          pattern is visible: uncommitted fixes do not survive a revert,
          and a revert aimed at one file can silently undo unrelated work
          living in the same working tree. */}
      {pct > 0 &&
        (dense ? (
          // Shelf mode: a bare 3px bar, no text row.
          <div className="pv-progress-track mt-1 h-[3px] w-full overflow-hidden rounded">
            <div className="pv-progress h-full" style={{ width: `${pct}%` }} />
          </div>
        ) : (
          <div className="mt-2 flex w-full items-center gap-2">
            <div className="pv-progress-track h-1.5 flex-1 overflow-hidden rounded">
              <div className="pv-progress h-full" style={{ width: `${pct}%` }} />
            </div>
            <span className="pv-fg w-12 text-right text-sm font-medium tabular-nums">
              {pct}%
            </span>
          </div>
        ))}

      {/* Highlights link. Only renders when there's something to look at.
          Tucked below the progress bar so it doesn't compete visually
          with the cover + title. Tapping opens the per-book Notebook
          (HighlightsView) — the "Amazon notebook" pattern. */}
      {!dense && highlightCount > 0 && (
        <Link
          to={`/highlights/${book.id}`}
          className="pv-fg-soft mt-2 block text-center text-xs active:opacity-60"
        >
          {highlightCount} highlight{highlightCount === 1 ? "" : "s"} ›
        </Link>
      )}
    </div>
  );
}

/** Normalize "Last, First" → "First Last" for display. EPUB metadata
 *  often stores authors surname-first; the natural reading order is
 *  first-last. Untouched when there's no comma (already natural-order)
 *  or when there are multiple commas (likely a multi-author list, where
 *  the swap would corrupt it). */
function displayAuthor(raw?: string): string | undefined {
  if (!raw) return raw;
  const s = raw.trim();
  if (!s) return s;
  // Ignore multi-author lists with ; or with more than one comma.
  if (s.includes(";")) return s;
  const parts = s.split(",");
  if (parts.length !== 2) return s;
  const last = parts[0].trim();
  const first = parts[1].trim();
  if (!last || !first) return s;
  return `${first} ${last}`;
}

/** Native-language names for the library language chip. Fallback is the
 *  uppercased primary subtag, so unknown languages still get a chip. */
const LANGUAGE_LABELS: Record<string, string> = {
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  ja: "日本語",
  zh: "中文",
  nl: "Nederlands",
  el: "Ελληνικά",
  la: "Latina",
  pl: "Polski",
  sv: "Svenska",
  da: "Dansk",
  fi: "Suomi",
};

/** Chip text for a book's dc:language — or undefined for English (or no
 *  declared language), where a chip would just be noise. */
function languageChip(raw?: string): string | undefined {
  if (!raw) return undefined;
  const primary = raw.trim().toLowerCase().split(/[-_]/)[0];
  if (!primary || primary === "en" || primary === "eng") return undefined;
  return LANGUAGE_LABELS[primary] ?? primary.toUpperCase();
}

/** A small procedural book-cover-style fallback used for books whose EPUB
 *  doesn't carry a cover image. Deterministic background colour seeded
 *  from the title so the same book always renders the same colour, with
 *  the title in a serif and the author below. Cheap and never network. */
function FallbackCover({ title, author }: { title: string; author?: string }) {
  const palette = ["#3b3b3b", "#5b4636", "#4a3a52", "#3a4a52", "#52473a", "#3a523f"];
  let h = 0;
  for (let i = 0; i < title.length; i++) h = ((h << 5) - h + title.charCodeAt(i)) | 0;
  const bg = palette[Math.abs(h) % palette.length];
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center"
      style={{ background: bg, color: "#f5f0e6" }}
    >
      <span
        className="font-serif text-sm leading-tight"
        style={{ wordBreak: "break-word" }}
      >
        {title}
      </span>
      <span
        className="text-[10px] italic leading-tight opacity-80"
        style={{ wordBreak: "break-word" }}
      >
        {author || " "}
      </span>
    </div>
  );
}
